import { useState, useMemo } from "react";
import { ArrowLeft, Save, CreditCard } from "lucide-react";
import { useSuppliers } from "../context/SuppliersContext";
import { usePaymentVoucher } from "../context/PaymentVoucherContext";
import { useNavigate } from "react-router-dom";

export default function CreatePaymentVoucher() {
  const navigate = useNavigate();
  const { suppliers } = useSuppliers();
  const { createPaymentVoucher } = usePaymentVoucher();

  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [search, setSearch] = useState("");
  const [amountPaid, setAmountPaid] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const outstanding = Number(selectedSupplier?.outstanding || 0);
  const paid        = Number(amountPaid || 0);
  const balance     = outstanding + paid;

  const filteredSuppliers = useMemo(() => {
    const q = search.toLowerCase();
    return (suppliers || []).filter((s) =>
      `${s.first_name} ${s.last_name}`.toLowerCase().includes(q) ||
      s.phone?.toLowerCase().includes(q) ||
      s.email?.toLowerCase().includes(q)
    );
  }, [search, suppliers]);

  const resetForm = () => {
    setSelectedSupplier(null);
    setAmountPaid("");
    setNote("");
  };

  const handleSubmit = async () => {
    if (!selectedSupplier) return alert("Select supplier");
    if (!amountPaid)        return alert("Enter amount paid");

    const payload = {
      user_id:  selectedSupplier.id,
      item_id:  1,
      quantity: 1,
      price:    0,
      amount:   outstanding,
      paid,
      balance,
      note:     note || "-",
    };

    try {
      setLoading(true);
      await createPaymentVoucher(payload);
      alert("Payment Voucher Created Successfully");
      resetForm();
      navigate("/payment-voucher");
    } catch (err) {
      alert(err.message || "Error creating voucher");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-gray-50 p-4 md:p-6 font-sans">
      <div className="mx-auto max-w-5xl space-y-5">

        {/* Header */}
        <div className="flex justify-between items-center bg-white p-5 rounded-2xl shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 flex items-center justify-center rounded-xl bg-teal-600 text-white">
              <CreditCard size={20} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Create Payment Voucher</h1>
              <p className="text-sm text-gray-400">
                {selectedSupplier ? "Enter payment details" : "Select a supplier to continue"}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate("/payment-voucher")}
            className="flex items-center gap-2 border border-gray-200 px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft size={15} /> Back
          </button>
        </div>

        {/* Step 1 — Select Supplier */}
        {!selectedSupplier && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 bg-white">
              <h2 className="font-semibold text-gray-700">Select Supplier</h2>
              <p className="text-sm text-gray-400 mt-0.5">Search and select a supplier to continue</p>
            </div>

            <div className="p-5">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, phone or email..."
                className="w-full h-10 border border-gray-200 rounded-xl px-4 bg-gray-50 text-sm outline-none focus:border-teal-400"
              />
            </div>

            <div className="overflow-x-auto px-5 pb-6">
              <table className="w-full text-sm min-w-[600px]">
                <thead className="bg-teal-700 text-white text-xs font-semibold">
                  <tr>
                    {["S.No", "Supplier", "Phone", "Email", "Action"].map((col) => (
                      <th key={col} className={`px-4 py-3 text-left ${col === "Action" ? "text-center" : ""}`}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredSuppliers.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-10 text-gray-400">No suppliers found</td>
                    </tr>
                  ) : (
                    filteredSuppliers.map((s, i) => (
                      <tr key={s.id} className="border-t border-gray-100 hover:bg-white">
                        <td className="px-4 py-3 text-gray-400">{i + 1}</td>
                        <td className="px-4 py-3 font-semibold text-gray-800">{s.first_name} {s.last_name}</td>
                        <td className="px-4 py-3 text-teal-600">{s.phone}</td>
                        <td className="px-4 py-3 text-teal-600">{s.email}</td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => setSelectedSupplier(s)}
                            className="px-3 py-1.5 text-white text-xs rounded-lg bg-teal-600 hover:bg-teal-700 transition-colors"
                          >
                            Select
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Step 2 — Payment Form */}
        {selectedSupplier && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 bg-white">
              <h2 className="font-semibold text-gray-700">Payment Information</h2>
              <p className="text-sm text-gray-400 mt-0.5">Fill in the payment details below</p>
            </div>

            <div className="p-6 space-y-6">

              {/* Supplier Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <InfoCard label="Supplier Name" value={`${selectedSupplier.first_name} ${selectedSupplier.last_name}`} />
                <InfoCard label="Phone"         value={selectedSupplier.phone} />
                <InfoCard label="Email"         value={selectedSupplier.email} />
                <InfoCard label="Outstanding"   value={`₹ ${outstanding}`} highlight />
              </div>

              {/* Inputs */}
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Amount Paid">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={amountPaid}
                    onChange={(e) => setAmountPaid(e.target.value)}
                    className="w-full mt-2 h-11 border border-gray-200 rounded-xl px-4 bg-gray-50 text-sm outline-none focus:border-teal-400"
                  />
                </Field>

                <Field label="Balance">
                  <input
                    value={balance}
                    readOnly
                    className="w-full mt-2 h-11 border border-gray-100 rounded-xl px-4 bg-gray-50 text-sm text-gray-500 cursor-not-allowed"
                  />
                </Field>
              </div>

              {/* Note */}
              <Field label="Note">
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  className="w-full mt-2 border border-gray-200 rounded-xl p-3 bg-gray-50 text-sm outline-none focus:border-teal-400 resize-none"
                />
              </Field>

              {/* Actions */}
              <div className="flex justify-end gap-3 border-t border-gray-100 pt-5">
                <button
                  onClick={() => setSelectedSupplier(null)}
                  className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex items-center gap-2 px-5 py-2 rounded-xl text-white text-sm font-medium bg-teal-600 hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save size={14} />
                  {loading ? "Saving..." : "Create Voucher"}
                </button>
              </div>

            </div>
          </div>
        )}

      </div>
    </main>
  );
}

function InfoCard({ label, value, highlight }) {
  return (
    <div className={`p-3 rounded-xl border ${highlight ? "bg-red-50 border-red-100" : "bg-white border-gray-100"}`}>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="font-semibold text-gray-800 mt-0.5">{value}</p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-sm font-semibold text-gray-700">{label}</label>
      {children}
    </div>
  );
}